// slices/feeSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api"

export const fetchFees = createAsyncThunk("fees/fetchFees", async (_, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    const response = await axios.get(`${API_URL}/fees`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!response.data?.data) {
      throw new Error("Invalid response format")
    }
    return response.data.data
  } catch (error) {
    console.error("fetchFees error:", error)
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

export const fetchFeeStats = createAsyncThunk("fees/fetchFeeStats", async (_, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    const response = await axios.get(`${API_URL}/fees/stats`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!response.data?.data) {
      throw new Error("Invalid response format")
    }
    return response.data.data
  } catch (error) {
    console.error("fetchFeeStats error:", error)
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

export const fetchDepartmentFeeSummary = createAsyncThunk(
  "fees/fetchDepartmentFeeSummary",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token")
      const response = await axios.get(`${API_URL}/fees/department-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.data?.data) {
        throw new Error("Invalid response format")
      }
      return response.data.data
    } catch (error) {
      console.error("fetchDepartmentFeeSummary error:", error)
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  },
)

export const createFee = createAsyncThunk("fees/createFee", async (feeData, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    const response = await axios.post(`${API_URL}/fees`, feeData, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!response.data?.data) {
      throw new Error("Invalid response format")
    }
    return response.data.data
  } catch (error) {
    console.error("createFee error:", error)
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

export const updateFee = createAsyncThunk("fees/updateFee", async ({ id, data }, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    const response = await axios.put(`${API_URL}/fees/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    })
    console.log("updateFee response:", response.data)
    return response.data.data || { fee_id: id, ...data } // Fallback if response.data.data is empty
  } catch (error) {
    console.error("updateFee error:", error.response?.data || error.message)
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

export const deleteFee = createAsyncThunk("fees/deleteFee", async (id, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    await axios.delete(`${API_URL}/fees/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    return id
  } catch (error) {
    console.error("deleteFee error:", error)
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

const feeSlice = createSlice({
  name: "fees",
  initialState: {
    fees: [],
    stats: null,
    departmentSummary: null,
    loading: false,
    loadingDepartment: false,
    error: null,
    message: { text: "", type: "" },
  },
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearMessage: (state) => {
      state.message = { text: "", type: "" }
    },
    setMessage: (state, action) => {
      state.message = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFees.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchFees.fulfilled, (state, action) => {
        state.loading = false
        state.fees = action.payload
      })
      .addCase(fetchFees.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.message = { text: `Error fetching fees: ${action.payload}`, type: "error" }
      })
      .addCase(fetchFeeStats.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchFeeStats.fulfilled, (state, action) => {
        state.loading = false
        state.stats = action.payload
      })
      .addCase(fetchFeeStats.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.message = { text: `Error fetching stats: ${action.payload}`, type: "error" }
      })
      .addCase(createFee.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createFee.fulfilled, (state, action) => {
        state.loading = false
        state.fees.push(action.payload)
        state.message = { text: "Fee created successfully!", type: "success" }
      })
      .addCase(createFee.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.message = { text: `Error creating fee: ${action.payload}`, type: "error" }
      })
      .addCase(updateFee.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateFee.fulfilled, (state, action) => {
        state.loading = false
        state.message = { text: "Fee updated successfully!", type: "success" }
        if (action.payload && action.payload.fee_id) {
          console.log("updateFee.fulfilled payload:", action.payload)
          const index = state.fees.findIndex((f) => f.fee_id === action.payload.fee_id)
          if (index !== -1) {
            state.fees[index] = action.payload
          } else {
            state.fees.push(action.payload)
          }
        } else {
          console.error("Invalid payload in updateFee.fulfilled:", action.payload)
          state.error = "Invalid response from server"
        }
      })
      .addCase(updateFee.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.message = { text: `Error updating fee: ${action.payload}`, type: "error" }
      })
      .addCase(deleteFee.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteFee.fulfilled, (state, action) => {
        state.loading = false
        state.fees = state.fees.filter((f) => f.fee_id !== action.payload)
        state.message = { text: "Fee deleted successfully!", type: "success" }
      })
      .addCase(deleteFee.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.message = { text: `Error deleting fee: ${action.payload}`, type: "error" }
      })
      .addCase(fetchDepartmentFeeSummary.pending, (state) => {
        state.loadingDepartment = true
        state.error = null
      })
      .addCase(fetchDepartmentFeeSummary.fulfilled, (state, action) => {
        state.loadingDepartment = false
        state.departmentSummary = action.payload
      })
      .addCase(fetchDepartmentFeeSummary.rejected, (state, action) => {
        state.loadingDepartment = false
        state.error = action.payload
        state.message = { text: `Error fetching department summary: ${action.payload}`, type: "error" }
      })
  },
})

export const { clearError, clearMessage, setMessage } = feeSlice.actions
export default feeSlice.reducer
